import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, Tag, Popconfirm, message, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BellOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const SystemNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/system-notifications');
      setNotifications(response.data.items || []);
    } catch (error) {
      message.error('Failed to fetch notifications');
      console.error('fetchNotifications error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);

    if (record) {
      form.setFieldsValue({
        ...record,
        expires_at: record.expires_at ? dayjs(record.expires_at) : null,
      });
    } else {
      form.resetFields();
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        expires_at: values.expires_at ? values.expires_at.toISOString() : null,
      };

      if (editingRecord) {
        await axios.put(`/api/system-notifications/${editingRecord.id}`, data);
        message.success('Notification updated successfully');
      } else {
        await axios.post('/api/system-notifications', data);
        message.success('Notification created successfully');
      }

      closeModal();
      fetchNotifications();
    } catch (error) {
      message.error('Failed to save notification');
      console.error('handleSubmit error:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/system-notifications/${id}`);
      message.success('Notification deleted successfully');
      fetchNotifications();
    } catch (error) {
      message.error('Failed to delete notification');
      console.error('handleDelete error:', error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const colors = {
          info: 'blue',
          success: 'green',
          warning: 'orange',
          error: 'red',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const colors = {
          low: 'green',
          normal: 'blue',
          high: 'orange',
          urgent: 'red',
        };
        return <Tag color={colors[priority] || 'default'}>{priority}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this notification?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <BellOutlined style={{ marginRight: 8 }} />
            System Notifications
          </Title>
          <Typography.Text type="secondary">
            Create and manage notifications visible to all users
          </Typography.Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Create Notification
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingRecord ? 'Edit Notification' : 'Create Notification'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: 'info',
            priority: 'normal',
            target_audience: 'all',
            active: true,
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter notification title' }]}
          >
            <Input placeholder="Notification title" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter notification message' }]}
          >
            <TextArea
              rows={4}
              placeholder="Notification message content"
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="type"
              label="Type"
              style={{ flex: 1 }}
            >
              <Select>
                <Option value="info">Info</Option>
                <Option value="success">Success</Option>
                <Option value="warning">Warning</Option>
                <Option value="error">Error</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="priority"
              label="Priority"
              style={{ flex: 1 }}
            >
              <Select>
                <Option value="low">Low</Option>
                <Option value="normal">Normal</Option>
                <Option value="high">High</Option>
                <Option value="urgent">Urgent</Option>
              </Select>
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="target_audience"
              label="Target Audience"
              style={{ flex: 1 }}
            >
              <Select>
                <Option value="all">All Users</Option>
                <Option value="clients">Clients Only</Option>
                <Option value="agencies">Agencies Only</Option>
                <Option value="admins">Admins Only</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="active"
              label="Active"
              style={{ flex: 1 }}
              valuePropName="checked"
            >
              <Select>
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="expires_at"
            label="Expires At (Optional)"
            extra="Leave empty for notifications that don't expire"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select expiration date"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
            <Button onClick={closeModal}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingRecord ? 'Update' : 'Create'} Notification
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SystemNotifications;
